import React, { useState, useEffect } from "react";
import API from "../../API/newIndex";
import { XLg, InfoCircle } from "react-bootstrap-icons";
import {
  ButtonComponent,
  InputComponent,
  SelectComponent,
} from "@shopeasy-dashboard-ui/ui-library";
import TextareaComponent from "../FormInput/TextareaComponent";

const AddReverseWithdrawalModal = ({ onClose, onSuccess }) => {
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [transactionType, setTransactionType] = useState("product"); // product, order, other
  const [balanceType, setBalanceType] = useState("debit"); // debit, credit
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");

  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedOrder] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchVendorsList = async () => {
      try {
        const res = await API({
          url: "/admin/vendor/list",
          method: "get",
          params: { page: 1, limit: 100 },
        });
        if (res?.data?.data) {
          setVendors(res.data.data.records || res.data.data.vendors || []);
        }
      } catch (err) {
        console.error("Error fetching vendor dropdown list:", err);
      }
    };
    fetchVendorsList();
  }, []);

  useEffect(() => {
    if (selectedVendor && transactionType === "product") {
      const fetchVendorProducts = async () => {
        try {
          const res = await API({
            url: `/admin/vendor/products/${selectedVendor}`,
            method: "get",
          });
          if (res?.data?.data) {
            setProducts(res.data.data.products || res.data.data.records || []);
          }
        } catch (err) {
          console.error("Error fetching products:", err);
        }
      };
      fetchVendorProducts();
    }
  }, [selectedVendor, transactionType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!selectedVendor) {
      setError("Please select a vendor.");
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setError("Please enter a valid reverse withdrawal amount.");
      return;
    }

    setSubmitting(true);
    try {
      await API({
        url: "/admin/vendor/reverse-withdrawal/create",
        method: "post",
        data: {
          vendor: selectedVendor,
          transaction_type: transactionType,
          product: transactionType === "product" ? selectedProduct : undefined,
          order: transactionType === "order" ? selectedOrder : undefined,
          balance_type: balanceType,
          amount: Number(amount),
          notes,
        },
      });
      onSuccess();
    } catch (err) {
      console.error("Create reverse withdrawal error:", err);
      setError(
        err?.response?.data?.message || err.message || "Failed to create reverse withdrawal."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="secondary-bg-color w-full max-w-lg rounded-[12px] shadow-2xl overflow-hidden border main-border-color my-8 animate-fadeIn text-[var(--primary-text-color)]">
        {/* Modal Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b main-border-color">
          <h2 className="text-lg font-bold primary-text-color">
            Add New Reverse Withdrawal
          </h2>
          <ButtonComponent
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="!p-1.5 table-text hover:primary-text-color hover:bg-[var(--focus-tab-color)]"
          >
            <XLg size={16} />
          </ButtonComponent>
        </div>

        {/* Modal Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-sm">
          {error && (
            <div className="p-3 text-xs bg-rose-50 text-rose-600 rounded-lg border border-rose-200 font-medium">
              {error}
            </div>
          )}

          {/* 1. Select Vendor */}
          <SelectComponent
            label="Select Vendor"
            required={true}
            value={selectedVendor}
            onChange={(e) => setSelectedVendor(e.target.value)}
            options={vendors.map((v) => ({
              value: v._id,
              label: `${v.shop_name || v.name} (${v.email})`,
            }))}
            placeholder="Search or select vendor..."
          />

          {/* 2. Transaction Type */}
          <div>
            <label className="block text-xs font-semibold primary-text-color mb-1">
              Transaction Type
            </label>
            <div className="grid grid-cols-3 gap-2">
              {["product", "order", "other"].map((type) => (
                <button
                  type="button"
                  key={type}
                  onClick={() => setTransactionType(type)}
                  className={`py-2 px-3 rounded-lg text-xs font-semibold capitalize border transition-all flex items-center justify-center gap-1.5 ${transactionType === type
                    ? "bg-[var(--btn-bg-primary)] text-white border-[var(--btn-bg-primary)] shadow-sm"
                    : "secondary-bg-color primary-text-color border main-border-color hover:bg-[var(--focus-tab-color)]"
                    }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* 3. Conditional: Select Product */}
          {transactionType === "product" && (
            <SelectComponent
              label="Select Product"
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              options={products.map((p) => ({
                value: p._id,
                label: p.product_name,
              }))}
              placeholder="Search or select product..."
            />
          )}

          {/* 4. Withdrawal Balance Type */}
          <div>
            <div className="flex items-center gap-1 mb-1">
              <label className="text-xs font-semibold primary-text-color">
                Withdrawal Balance Type
              </label>
              <InfoCircle className="table-text" size={12} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {["debit", "credit"].map((bType) => (
                <button
                  type="button"
                  key={bType}
                  onClick={() => setBalanceType(bType)}
                  className={`py-2 px-3 rounded-lg text-xs font-semibold capitalize border transition-all ${balanceType === bType
                    ? "bg-[var(--btn-bg-primary)] text-white border-[var(--btn-bg-primary)] shadow-sm"
                    : "secondary-bg-color primary-text-color border main-border-color hover:bg-[var(--focus-tab-color)]"
                    }`}
                >
                  {bType}
                </button>
              ))}
            </div>
          </div>

          {/* 5. Reverse Withdrawal Amount */}
          <InputComponent
            label="Reverse Withdrawal Amount"
            type="number"
            required={true}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            prefix={<span className="table-text font-semibold">$</span>}
          />

          {/* 6. Notes */}
          <TextareaComponent
            label="Notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Write reverse withdrawal note"
          />

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-3 border-t main-border-color">
            <ButtonComponent variant="outline" size="md" onClick={onClose}>
              Cancel
            </ButtonComponent>
            <ButtonComponent type="submit" variant="primary" size="md" loading={submitting}>
              Submit
            </ButtonComponent>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddReverseWithdrawalModal;
